import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { Property } from '../types/property.types';

interface AnalyticsProps {
  properties: Property[];
}

const Analytics: React.FC<AnalyticsProps> = ({ properties }) => {
  // Colores para los gráficos
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  // Datos por tipo de propiedad
  const propertyTypeData = useMemo(() => {
    const typeCount = properties.reduce((acc, property) => {
      const type = property.tipo_inmueble || 'Sin especificar';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(typeCount).map(([name, value]) => ({ name, value }));
  }, [properties]);

  // Datos por ciudad
  const cityData = useMemo(() => {
    const cityCount = properties.reduce((acc, property) => {
      const city = property.ciudad || 'Sin especificar';
      acc[city] = (acc[city] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(cityCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 ciudades
  }, [properties]);

  // Datos por estado
  const statusData = useMemo(() => {
    const statusCount = properties.reduce((acc, property) => {
      const status = property.estado || 'Sin especificar';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(statusCount).map(([name, value]) => ({ name, value }));
  }, [properties]);

  // Datos por rango de precios
  const priceRangeData = useMemo(() => {
    const ranges = {
      '< $500K': 0,
      '$500K - $1M': 0,
      '$1M - $2M': 0,
      '$2M - $5M': 0,
      '> $5M': 0,
      'Sin precio': 0
    };

    properties.forEach(property => {
      const price = property.valor_venta || property.valor_arriendo || property.precio;
      
      if (!price || price === 0) {
        ranges['Sin precio']++;
      } else if (price < 500000000) {
        ranges['< $500K']++;
      } else if (price < 1000000000) {
        ranges['$500K - $1M']++;
      } else if (price < 2000000000) {
        ranges['$1M - $2M']++;
      } else if (price < 5000000000) {
        ranges['$2M - $5M']++;
      } else {
        ranges['> $5M']++;
      }
    });

    return Object.entries(ranges)
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  }, [properties]);

  // Estadísticas generales
  const stats = useMemo(() => {
    const totalProperties = properties.length;
    const availableProperties = properties.filter(p => p.estado === 'Disponible').length;
    const averagePrice = properties.reduce((sum, p) => {
      const price = p.valor_venta || p.valor_arriendo || p.precio || 0;
      return sum + price;
    }, 0) / (properties.filter(p => (p.valor_venta || p.valor_arriendo || p.precio)).length || 1);

    const averageArea = properties.reduce((sum, p) => {
      const area = p.area_total || p.area_privada || 0;
      return sum + area;
    }, 0) / (properties.filter(p => p.area_total || p.area_privada).length || 1);

    return {
      totalProperties,
      availableProperties,
      averagePrice,
      averageArea,
      occupancyRate: ((totalProperties - availableProperties) / totalProperties * 100) || 0
    };
  }, [properties]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('es-CO').format(Math.round(value));
  };

  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">📊</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No hay datos para mostrar
        </h3>
        <p className="text-gray-500">
          Agrega algunas propiedades para ver las estadísticas
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Análisis de Propiedades</h2>
        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
          {properties.length} propiedades analizadas
        </span>
      </div>

      {/* Estadísticas Generales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">🏠</div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Total Propiedades</div>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(stats.totalProperties)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">✅</div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Disponibles</div>
              <div className="text-2xl font-bold text-green-600">{formatNumber(stats.availableProperties)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">💰</div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Precio Promedio</div>
              <div className="text-lg font-bold text-blue-600">{formatCurrency(stats.averagePrice)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">📐</div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Área Promedio</div>
              <div className="text-2xl font-bold text-purple-600">{formatNumber(stats.averageArea)} m²</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">📊</div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Tasa Ocupación</div>
              <div className="text-2xl font-bold text-orange-600">{stats.occupancyRate.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Propiedades por tipo */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Propiedades por Tipo</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={propertyTypeData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
              >
                {propertyTypeData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Propiedades por ciudad */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Propiedades por Ciudad (Top 10)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={cityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Distribución por estado */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado de Propiedades</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Rangos de precios */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribución por Rango de Precios</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={priceRangeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">📋 Insights Rápidos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-blue-800">
              <strong>Tipo más común:</strong> {propertyTypeData.length > 0 ? propertyTypeData.reduce((a, b) => a.value > b.value ? a : b).name : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-blue-800">
              <strong>Ciudad principal:</strong> {cityData.length > 0 ? cityData[0].name : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-blue-800">
              <strong>Disponibilidad:</strong> {stats.availableProperties} de {stats.totalProperties} propiedades ({((stats.availableProperties / stats.totalProperties) * 100).toFixed(1)}%)
            </p>
          </div>
          <div>
            <p className="text-blue-800">
              <strong>Inventario:</strong> {stats.totalProperties > 50 ? 'Alto' : stats.totalProperties > 20 ? 'Medio' : 'Bajo'} volumen de propiedades
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;